import React, { useRef } from 'react';
import { useEnteredView } from '@fiverr-private/hooks';
import { translate } from '@fiverr-private/i18n-react';
import { Container } from '@fiverr-private/layout_components';
import { Rating } from '@fiverr-private/orca';
import { FederatedSeller, formatBigNumbers, useSellerData } from '@fiverr-private/seller_pages_toolbox';
import { Text } from '@fiverr-private/typography';
import { useSelectedRole } from '../../../hooks/useSelectedRole';
import { useBI } from '../../BIContext';
import { TabName, scrollToTab } from '../../StickyHeader/tab-names';
import { ratingStarClass, ratingScoreClass } from './Reviews.ve.css';

interface Props {
    sellerRoleId?: string;
}

export const Reviews = ({ sellerRoleId }: Props) => {
    const observableRef = useRef<HTMLDivElement>(null);
    const { rating } = useSellerData(selectSellerSlice);
    const selectedRole = useSelectedRole();
    const {
        reportSellerHighlightsRatingImpression,
        reportSellerHighlightsReviewsClicked,
        reportSellerHighlightsReviewsImpression,
    } = useBI();

    const { score, count } = (selectedRole ? selectedRole.rating : rating) || {};
    const formattedScore = score ? Number(score.toFixed(1)) : null;

    const label = translate(
        `seller_page_perseus.professions_page.highlights.reviews_count.${count === 1 ? 'one' : 'many'}`,
        {
            params: {
                count: formatBigNumbers(count || 0),
            },
        }
    );

    useEnteredView(observableRef, {
        threshold: 1,
        onEntered: () => {
            reportSellerHighlightsReviewsImpression({ label });
            reportSellerHighlightsRatingImpression({ label: `${formattedScore}` });
        },
    });

    const onRatingsClick = () => {
        reportSellerHighlightsReviewsClicked({ label });
        scrollToTab({ tabId: TabName.Reviews, roleId: sellerRoleId });
    };

    return (
        <Container
            ref={observableRef}
            paddingTop={{
                default: '0.5',
                md: '1',
                lg: '1.5',
            }}
            cursor="pointer"
            onClick={onRatingsClick}
        >
            <Rating
                score={formattedScore}
                color={(Rating as any).COLORS.BLACK}
                starClassName={ratingStarClass}
                scoreClassName={ratingScoreClass}
            />
            <Text
                size="b_sm"
                fontWeight="semibold"
                decoration="underline"
                cursor="pointer"
                marginTop={{
                    default: '1.5',
                    lg: '2',
                }}
                whiteSpace="nowrap"
            >
                {label}
            </Text>
        </Container>
    );
};

const selectSellerSlice = (seller: FederatedSeller) => ({
    rating: seller.rating,
});
